import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { loadAccounts, loadGroups } from '../Action/api';

// ---------------------------------------------------------------------------
// Data-mapping helpers  (same logic as the old processAccountsData /
// processGroupsData functions that lived inside Settings/index.js)
// ---------------------------------------------------------------------------
const mapAccount = (account) => ({
  id: account.accountId,
  accountId: account.accountId,
  type: account.type || 'Unknown',
  provider: account.provider || 'Unknown',
  accountName: account.accountName || 'Unknown',
  identifier: account.username || account.senderId || account.fromEmail || 'N/A',
  regionOrFromEmail: account.region || account.fromEmail || '',
  connectedBy: account.connectedBy || 'Unknown',
  status: account.status,
  expiry: account.tokenExpiry || account.accessTokenExpiry || '',
  accessTokenExpiry: account.accessTokenExpiry || account.tokenExpiry || '',
  pageTokenExpiry: account.pageTokenExpiry || '',
  group: account.groupName || '',
  groupId: Array.isArray(account.groupId)
    ? account.groupId[0]
    : account.groupId || '',
  description: '',
  notes: account.notes || '',
  appLink: account.appLink || '',
  createdAt: account.CreatedAt,
  updatedAt: account.UpdatedAt,
  // Preserve original API fields for editing
  username: account.username,
  accessToken: account.accessToken,
  tokenExpiry: account.tokenExpiry || account.accessTokenExpiry,
  pageToken: account.pageToken || '',
  // Email specific
  apiKey: account.apiKey,
  smtpHost: account.smtpHost,
  smtpPort: account.smtpPort,
  smtpUsername: account.smtpUsername,
  smtpPassword: account.smtpPassword,
  fromEmail: account.fromEmail,
  fromName: account.fromName,
  replyToEmail: account.replyToEmail,
  // SMS specific
  authToken: account.authToken,
  senderId: account.senderId,
  country: account.country,
  region: account.region,
  entityType: 'Account',
});

const mapGroup = (group) => ({
  id: group.groupId,
  groupId: group.groupId,
  groupName: group.groupName,
  groupType: group.groupType,
  accountsCount: group.accountIds ? group.accountIds.length : 0,
  createdBy: 'System',
  status: group.status,
  accounts: [],
  accountIds: group.accountIds || [],
  country: group.country
    ? {
        countryCode: group.country.split('(').pop()?.replace(')', '') || 'N/A',
        countryName: group.country.split('(')[0]?.trim() || 'N/A',
        zoneName: 'N/A',
        regionName: 'N/A',
        territoryName: 'N/A',
      }
    : {
        countryCode: 'N/A',
        countryName: 'N/A',
        zoneName: 'N/A',
        regionName: 'N/A',
        territoryName: 'N/A',
      },
  description: group.description || '',
  notes: group.notes || '',
  createdAt: group.CreatedAt,
  updatedAt: group.UpdatedAt,
  entityType: group.entityType || 'Account',
});

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------
export const fetchSettingsAccounts = createAsyncThunk(
  'settings/fetchAccounts',
  async (_, { rejectWithValue }) => {
    try {
      const result = await loadAccounts();
      if (result.success) {
        // isEmpty + no error = genuinely empty list — return []
        if (result.isEmpty && !result.isError) return [];
        if (result.isEmpty) return rejectWithValue(result.message);
        return result.data.accounts.map(mapAccount);
      }
      return rejectWithValue(result.message || 'Failed to load accounts');
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load accounts');
    }
  }
);

export const fetchSettingsGroups = createAsyncThunk(
  'settings/fetchGroups',
  async (_, { rejectWithValue }) => {
    try {
      const result = await loadGroups();
      if (result.success) {
        if (result.isEmpty && !result.isError) return [];
        if (result.isEmpty) return rejectWithValue(result.message);
        return result.data.groups.map(mapGroup);
      }
      return rejectWithValue(result.message || 'Failed to load groups');
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load groups');
    }
  }
);

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------
const settingsSlice = createSlice({
  name: 'settings',
  initialState: {
    accounts: [],
    groups: [],
    /** 'idle' | 'loading' | 'succeeded' | 'failed' */
    accountsStatus: 'idle',
    groupsStatus: 'idle',
    accountsError: null,
    groupsError: null,
    accountsLastFetchedAt: null,
    groupsLastFetchedAt: null,
  },
  reducers: {
    /** Remove a single account from the store after a successful delete API call. */
    removeAccountFromStore: (state, action) => {
      state.accounts = state.accounts.filter(
        (a) => a.accountId !== action.payload
      );
    },
    /** Patch a single account in the store after a successful edit API call. */
    updateAccountInStore: (state, action) => {
      const idx = state.accounts.findIndex(
        (a) =>
          a.accountId === action.payload.accountId ||
          a.id === action.payload.id
      );
      if (idx !== -1) {
        state.accounts[idx] = { ...state.accounts[idx], ...action.payload };
      }
    },
    /** Remove a single group from the store after a successful delete API call. */
    removeGroupFromStore: (state, action) => {
      state.groups = state.groups.filter(
        (g) => g.groupId !== action.payload
      );
    },
    /** Patch a single group in the store after a successful edit API call. */
    updateGroupInStore: (state, action) => {
      const idx = state.groups.findIndex(
        (g) =>
          g.groupId === action.payload.groupId ||
          g.id === action.payload.id
      );
      if (idx !== -1) {
        state.groups[idx] = { ...state.groups[idx], ...action.payload };
      }
    },
    /**
     * Mark accounts as stale so the next mount will trigger a fresh fetch.
     * Call before dispatching fetchSettingsAccounts when you want to force-refresh.
     */
    invalidateAccounts: (state) => {
      state.accountsStatus = 'idle';
      state.accountsLastFetchedAt = null;
    },
    invalidateGroups: (state) => {
      state.groupsStatus = 'idle';
      state.groupsLastFetchedAt = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── Accounts ──────────────────────────────────────────────────────────
      .addCase(fetchSettingsAccounts.pending, (state) => {
        state.accountsStatus = 'loading';
        state.accountsError = null;
      })
      .addCase(fetchSettingsAccounts.fulfilled, (state, action) => {
        state.accountsStatus = 'succeeded';
        state.accounts = action.payload;
        state.accountsLastFetchedAt = Date.now();
        state.accountsError = null;
      })
      .addCase(fetchSettingsAccounts.rejected, (state, action) => {
        state.accountsStatus = 'failed';
        state.accountsError = action.payload || 'Failed to load accounts';
      })
      // ── Groups ────────────────────────────────────────────────────────────
      .addCase(fetchSettingsGroups.pending, (state) => {
        state.groupsStatus = 'loading';
        state.groupsError = null;
      })
      .addCase(fetchSettingsGroups.fulfilled, (state, action) => {
        state.groupsStatus = 'succeeded';
        state.groups = action.payload;
        state.groupsLastFetchedAt = Date.now();
        state.groupsError = null;
      })
      .addCase(fetchSettingsGroups.rejected, (state, action) => {
        state.groupsStatus = 'failed';
        state.groupsError = action.payload || 'Failed to load groups';
      });
  },
});

export const {
  removeAccountFromStore,
  updateAccountInStore,
  removeGroupFromStore,
  updateGroupInStore,
  invalidateAccounts,
  invalidateGroups,
} = settingsSlice.actions;

export default settingsSlice.reducer;
